import { useState } from 'react';
import { engagementsApi } from '../../../api/engagements.js';
import { filesApi } from '../../../api/files.js';
import { Modal } from '../../../components/common/Modal.jsx';
import { FormField, Textarea } from '../../../components/common/FormField.jsx';
import { ErrorAlert } from '../../../components/common/ErrorAlert.jsx';
import { formatDate } from '../../../utils/dates.js';

function SubmissionCard({ submission, engagementId, currentValue, onReviewed }) {
  const [modal, setModal] = useState(null); // 'approve'|'reject'|'return'
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const decide = async (decision) => {
    if ((decision === 'rejected' || decision === 'returned') && !note.trim()) {
      setError('A reviewer note is required');
      return;
    }
    setSaving(true);
    try {
      const result = await engagementsApi.reviewSubmission(
        engagementId, submission.id, { decision, note: note.trim() || null }
      );
      onReviewed(result);
      setModal(null);
      setNote('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (submission.reviewStatus !== 'pending') return null;

  return (
    <div className="card stack">
      <div className="between">
        <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)', textTransform: 'capitalize' }}>
          {submission.checklistKey.replace(/-/g, ' ')} submission
        </h4>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Submitted {formatDate(submission.submittedAt)}
        </span>
      </div>

      <div className="review-panel">
        <div className="review-col">
          <p className="review-col-header">Current approved content</p>
          {currentValue ? (
            typeof currentValue === 'string' ? (
              <p style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{currentValue}</p>
            ) : currentValue.fileId ? (
              <a href={filesApi.getUrl(currentValue.fileId)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                View current file
              </a>
            ) : <p style={{ color: 'var(--color-text-subtle)', fontSize: 'var(--text-sm)' }}>None</p>
          ) : <p style={{ color: 'var(--color-text-subtle)', fontSize: 'var(--text-sm)' }}>None</p>}
        </div>
        <div className="review-col">
          <p className="review-col-header">Submitted content</p>
          {submission.textValue ? (
            <p style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{submission.textValue}</p>
          ) : submission.fileId ? (
            <a href={filesApi.getUrl(submission.fileId)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
              View submitted file
            </a>
          ) : <p style={{ color: 'var(--color-text-subtle)', fontSize: 'var(--text-sm)' }}>—</p>}
        </div>
      </div>

      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      <div className="inline">
        <button className="btn btn-primary btn-sm" onClick={() => { setModal('approve'); setNote(''); }}>Approve</button>
        <button className="btn btn-secondary btn-sm" onClick={() => { setModal('return'); setNote(''); }}>Return with note</button>
        <button className="btn btn-danger btn-sm" onClick={() => { setModal('reject'); setNote(''); }}>Reject</button>
      </div>

      <Modal
        open={Boolean(modal)}
        onClose={() => setModal(null)}
        title={modal === 'approve' ? 'Approve submission' : modal === 'return' ? 'Return submission' : 'Reject submission'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
            <button
              className={`btn btn-${modal === 'approve' ? 'primary' : 'danger'}`}
              onClick={() => decide(modal)}
              disabled={saving}
            >
              {saving ? 'Saving…' : modal === 'approve' ? 'Approve' : modal === 'return' ? 'Return' : 'Reject'}
            </button>
          </>
        }
      >
        <div className="stack">
          {modal !== 'approve' && (
            <FormField label="Reviewer note" required id="reviewNote" error={error?.includes('note') ? error : null}>
              <Textarea
                id="reviewNote"
                value={note}
                onChange={(e) => { setNote(e.target.value); setError(null); }}
                placeholder={modal === 'return' ? 'Explain what changes are needed…' : 'Explain why this is rejected…'}
                rows={3}
              />
            </FormField>
          )}
          {modal === 'approve' && (
            <p style={{ fontSize: 'var(--text-sm)' }}>
              Approving will replace the current approved content with the submitted content.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export function ReviewTab({ engagement, onUpdate }) {
  const pending = (engagement.pendingSubmissions ?? []).filter((s) => s.reviewStatus === 'pending');

  const currentValues = {
    biography: engagement.bio,
    headshot: engagement.headshot,
    'agreement-rcvd': engagement.agreementFile,
  };

  if (pending.length === 0) {
    return (
      <div className="empty-state">
        <h3>No pending reviews</h3>
        <p>Speaker submissions will appear here when the speaker submits content through their portal.</p>
      </div>
    );
  }

  return (
    <div className="stack">
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        {pending.length} submission{pending.length !== 1 ? 's' : ''} awaiting review
      </p>
      {pending.map((sub) => (
        <SubmissionCard
          key={sub.id}
          submission={sub}
          engagementId={engagement.id}
          currentValue={currentValues[sub.checklistKey]}
          onReviewed={({ engagement: updated }) => onUpdate(updated)}
        />
      ))}
    </div>
  );
}
